import JSZip from 'jszip';

/**
 * Erstellt aus den übergebenen Dateien (inklusive Relativpfad) eine ZIP-Datei.
 * Wenn webkitRelativePath vorhanden ist, wird dieser Pfad in der ZIP-Struktur verwendet.
 */
export async function createZipFromFiles(files: File[], zipName: string = 'upload.zip'): Promise<File> {
  const zip = new JSZip();
  
  // Filtere System-Dateien automatisch heraus
  const cleanFiles = filterSystemFiles(files);
  
  cleanFiles.forEach(file => {
    const pathInZip = (file as any).webkitRelativePath && (file as any).webkitRelativePath !== ''
      ? (file as any).webkitRelativePath
      : file.name;
    zip.file(pathInZip, file);
  });
  const blob = await zip.generateAsync({ type: 'blob' });
  return new File([blob], zipName, { type: 'application/zip' });
}

/**
 * Erstellt eine ZIP-Datei für Mehrfach-Dienstleistungen aus Ordner-Strukturen.
 * Flacht die Unterordner-Struktur ab und organisiert nach Service-Ordnern.
 * 
 * @param files - Array von Dateien mit webkitRelativePath
 * @param zipName - Name der zu erstellenden ZIP-Datei
 * @returns Promise<File> - Die erstellte ZIP-Datei
 */
export async function createServiceZipFromFolders(files: File[], zipName: string = 'dienstleistungen.zip'): Promise<File> {
  const zip = new JSZip();
  
  // Filtere System-Dateien automatisch heraus
  const cleanFiles = filterSystemFiles(files);
  
  if (cleanFiles.length === 0) {
    throw new Error('Keine gültigen Dateien zum Zippen gefunden (nur System-Dateien vorhanden)');
  }
  
  console.log('🔍 Erstelle ZIP aus', cleanFiles.length, 'gefilterten Dateien');
  
  // Sammle alle Dateien gruppiert nach Service-Ordner (erste Ebene)
  const serviceGroups: { [serviceName: string]: File[] } = {};
  
  cleanFiles.forEach(file => {
    const relativePath = (file as any).webkitRelativePath || file.name;
    const pathParts = relativePath.split('/');
    
    // Nur Dateien in Ordnern verarbeiten
    if (pathParts.length > 1) {
      const serviceName = pathParts[0];
      
      if (!serviceGroups[serviceName]) {
        serviceGroups[serviceName] = [];
      }
      
      serviceGroups[serviceName].push(file);
          }
  });
  
  console.log('🗂️ Services gefunden:', Object.keys(serviceGroups).join(', '));
  
  // Erstelle ZIP-Struktur für jeden Service
  Object.entries(serviceGroups).forEach(([serviceName, serviceFiles]) => {
    serviceFiles.forEach(file => {
      // Für Dienstleistungen: Flache Struktur - alle Dateien direkt im Service-Ordner
      const fileNameInZip = `${serviceName}/${file.name}`;
      zip.file(fileNameInZip, file);
    });
  });
  
  const blob = await zip.generateAsync({ type: 'blob' });
  return new File([blob], zipName, { type: 'application/zip' });
}

/**
 * Validiert, ob die hochgeladenen Dateien eine gültige Ordner-Struktur für Services haben
 * 
 * @param files - Array von Dateien mit webkitRelativePath
 * @returns {{ isValid: boolean, errors: string[], serviceNames: string[] }}
 */
export function validateServiceFolderStructure(files: File[]): { 
  isValid: boolean; 
  errors: string[]; 
  serviceNames: string[] 
} {
  const errors: string[] = [];
  const serviceNames: Set<string> = new Set();
  const allowedExtensions = ['.pdf', '.docx', '.txt'];
  
  // System-Dateien die automatisch ignoriert werden sollen
  const systemFiles = [
    '.ds_store',
    'thumbs.db',
    'desktop.ini',
    '.gitkeep',
    '.gitignore',
    '__macosx'
  ];
  
  if (files.length === 0) {
    errors.push('Keine Dateien ausgewählt');
    return { isValid: false, errors, serviceNames: [] };
  }
  
  // Filtere System-Dateien automatisch heraus
  const validFiles = files.filter(file => {
    const fileName = file.name.toLowerCase();
    const isSystemFile = systemFiles.some(sysFile => fileName.includes(sysFile));
    
    if (isSystemFile) {
      console.log(`Ignoriere System-Datei: ${file.name}`);
      return false;
    }
    
    return true;
  });
  
  if (validFiles.length === 0) {
    errors.push('Keine gültigen Dateien gefunden (nur System-Dateien vorhanden)');
    return { isValid: false, errors, serviceNames: [] };
  }
  
  // Sammle Service-Namen und prüfe Struktur
  const serviceFileCount: { [serviceName: string]: number } = {};
  let hasFilesWithoutFolder = false;
  
  validFiles.forEach(file => {
    const relativePath = (file as any).webkitRelativePath || file.name;
    const pathParts = relativePath.split('/');
    
    console.log(`🔍 Validate File: ${file.name}`);
    console.log(`📂 Relativer Pfad: ${relativePath}`);
    console.log(`📋 Path Parts:`, pathParts);
    
    // Prüfe Datei-Extension
    const extension = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));
    if (!allowedExtensions.includes(extension)) {
      errors.push(`Unerlaubter Dateityp: ${file.name} (${extension}). Nur PDF, DOCX und TXT sind erlaubt.`);
      return;
    }
    
    // Prüfe Ordner-Struktur
    if (pathParts.length <= 1) {
      // Datei ohne Ordner
      hasFilesWithoutFolder = true;
      console.log(`❌ Datei ohne Ordner: ${file.name}`);
      return;
    }
    
    // Sammle Service-Namen (erste Ordner-Ebene)
    const serviceName = pathParts[0];
    console.log(`🏷️ Gefundener Service: ${serviceName}`);
    serviceNames.add(serviceName);
    
    // Zähle Dateien pro Service
    if (!serviceFileCount[serviceName]) {
      serviceFileCount[serviceName] = 0;
    }
    serviceFileCount[serviceName]++;
  });
  
  // Fehler für Dateien ohne Ordner
  if (hasFilesWithoutFolder) {
    errors.push('Einzelne Dateien ohne Ordner sind nicht erlaubt. Bitte organisieren Sie Ihre Dateien in Ordnern nach Dienstleistungen.');
  }
  
  // Prüfe auf leere Services
  Object.entries(serviceFileCount).forEach(([serviceName, fileCount]) => {
    if (fileCount === 0) {
      errors.push(`Ordner "${serviceName}" enthält keine gültigen Dateien.`);
      serviceNames.delete(serviceName);
    }
  });
  
  // Mindestens ein Service muss vorhanden sein
  if (serviceNames.size === 0) {
    errors.push('Keine gültigen Service-Ordner mit Dateien gefunden. Bitte stellen Sie sicher, dass Ihre Dateien in Ordnern organisiert sind.');
  }
  
  console.log(`📊 Gefundene Services: ${Array.from(serviceNames).join(', ')}`);
  console.log(`📈 Service-Datei-Anzahl:`, serviceFileCount);
  
  return {
    isValid: errors.length === 0,
    errors,
    serviceNames: Array.from(serviceNames)
  };
}

/**
 * Filtert System-Dateien aus einem File-Array heraus
 * 
 * @param files - Array von Dateien
 * @returns Gefiltertes Array ohne System-Dateien
 */
export function filterSystemFiles(files: File[]): File[] {
  const systemFiles = [
    '.ds_store',
    'thumbs.db', 
    'desktop.ini',
    '.gitkeep',
    '.gitignore',
    '__macosx'
  ];
  
  return files.filter(file => {
    const fileName = file.name.toLowerCase();
    const relativePath = ((file as any).webkitRelativePath || '').toLowerCase();
    
    const isSystemFile = systemFiles.some(sysFile => 
      fileName.includes(sysFile) || relativePath.includes(sysFile)
    );
    
    if (isSystemFile) {
      console.log(`🗑️ Ignoriere System-Datei: ${file.name}`);
      return false;
    }
    
    return true;
  });
} 